import { createReducer, on } from '@ngrx/store';

import { BetslipType } from '../../core/betslip-type';
import { BetslipPlaceBetActions } from '../betplacement/actions';
import { EditBetActions } from '../edit-bet/actions';
import BetslipErrorsActions from './actions';
import { BetslipError, CleanStrategy } from './errors/betslip-error';
import { DoubleBetPrevention } from './errors/general/double-bet-prevention';
import {
    errorCollectionUpdater,
    filterErrorState,
    getTypedPickErrors,
    modifyErrorState,
    updateKeyBasedErrorCollection,
} from './services/utils/betslip-errors-state-utils';
import {
    OverallMaxWinPerUserHintType,
    errorCollectionPusher,
    isNotifyUserChangeError,
    isOverallMaxWinPerUserExceededError,
    isPickStatusChangeError,
} from './services/utils/betslip-errors-utils';
import { BetslipTypePickErrors, IBetslipPickErrors, SlipErrors, defaultErrorState } from './state';

export const errorReducer = createReducer(
    defaultErrorState,
    on(BetslipErrorsActions.picksErrorsCheckFinished, (state, action) => {
        let hasChanges = false;

        const fullResult: BetslipTypePickErrors = { SINGLE: {}, COMBO: {}, SYSTEM: {}, TEASER: {}, EDIT_MY_BET: {}, BET_BUILDER: {} };

        for (const [type, newPickErrors] of getTypedPickErrors(action.errors)) {
            const newPickIds = Object.keys(newPickErrors);

            const typeResult: IBetslipPickErrors = {};

            for (const pickId of newPickIds) {
                if (pickId in state.pickErrors[type]) {
                    typeResult[pickId] = errorCollectionUpdater(state.pickErrors[type][pickId], newPickErrors[pickId]);
                    if (typeResult[pickId] !== state.pickErrors[type][pickId]) {
                        hasChanges = true;
                    }

                    continue;
                }

                typeResult[pickId] = newPickErrors[pickId];
                hasChanges = true;
            }

            const stateErrors = state.pickErrors[type];

            const oldPickIds = Object.keys(stateErrors);

            for (const oldPickId of oldPickIds.filter((oid) => !newPickIds.some((nid) => nid === oid))) {
                // Keep errors without client validation when they are missing from the newErrors.
                const errorsToKeep = stateErrors[oldPickId].filter((e) => !e.hasClientValidation);

                if (errorsToKeep.length === 0) {
                    continue;
                }

                typeResult[oldPickId] = errorsToKeep;
                hasChanges = true;
            }

            hasChanges = hasChanges || newPickIds.length !== oldPickIds.length;
            fullResult[type] = typeResult;
        }

        if (!hasChanges) {
            // No change. Do not mutate the state.
            return state;
        }

        return {
            ...state,
            pickErrors: fullResult,
        };
    }),
    on(BetslipErrorsActions.betslipErrorsCheckFinished, (state, action) => {
        const newBetslipErrors = errorCollectionUpdater(state.betslipErrors, action.errors);
        const slipErrors = updateKeyBasedErrorCollection(
            state.slipErrors,
            action.slipErrors,
            {},
            Object.keys(state.slipErrors),
            Object.keys(action.slipErrors),
        );

        const activeTypes = [
            BetslipType.Single,
            BetslipType.Combo,
            BetslipType.System,
            BetslipType.Teaser,
            BetslipType.EditBet,
            BetslipType.BetBuilder,
        ];

        const newTypeErrors = updateKeyBasedErrorCollection(
            state.betslipTypeErrors,
            action.typeSlipErrors,
            { SINGLE: [], COMBO: [], SYSTEM: [], TEASER: [], EDIT_MY_BET: [], BET_BUILDER: [] },
            activeTypes,
            activeTypes,
        );

        if (newBetslipErrors === state.betslipErrors && !slipErrors.hasChanged && !newTypeErrors.hasChanged) {
            // No change. Do not mutate the state.
            return state;
        }

        return {
            ...state,
            betslipErrors: newBetslipErrors,
            slipErrors: slipErrors.errors,
            betslipTypeErrors: newTypeErrors.errors,
        };
    }),
    on(BetslipErrorsActions.addPickError, (state, action) => {
        const { pickId, error } = action;
        const pickIdKey = pickId.toString();
        const currentPickErrors = state.pickErrors[action.betslipType][pickIdKey] || [];
        const newPickErrors = errorCollectionPusher(currentPickErrors, error);
        if (newPickErrors === currentPickErrors) {
            return state;
        }

        return {
            ...state,
            pickErrors: {
                ...state.pickErrors,
                [action.betslipType]: {
                    ...state.pickErrors[action.betslipType],
                    [pickIdKey]: newPickErrors,
                },
            },
        };
    }),
    on(BetslipErrorsActions.addBetslipError, (state, action) => {
        const currentErrors = state.betslipErrors || [];
        const newErrors = errorCollectionPusher(currentErrors, action.error);
        if (currentErrors === newErrors) {
            return state;
        }

        return {
            ...state,
            betslipErrors: newErrors,
        };
    }),
    on(BetslipErrorsActions.removeDoubleBetPreventionError, (state, action) => {
        state.betslipErrors = state.betslipErrors.filter((e) => !(e instanceof DoubleBetPrevention));

        state.slipErrors = Object.entries(state.slipErrors).reduce((aggr, curr) => {
            const [key, value] = curr;

            return {
                ...aggr,
                [key]: value.filter((e) => !(e instanceof DoubleBetPrevention)),
            };
        }, {} as SlipErrors);

        return state;
    }),
    on(BetslipErrorsActions.removeOfferChangedError, (state, _) => {
        const pickErrors: BetslipTypePickErrors = { SINGLE: {}, COMBO: {}, SYSTEM: {}, TEASER: {}, EDIT_MY_BET: {}, BET_BUILDER: {} };

        for (const [type, newPickErrors] of getTypedPickErrors(state.pickErrors)) {
            const pickIds = Object.keys(newPickErrors);

            for (const pickId of pickIds) {
                pickErrors[type][pickId] = state.pickErrors[type][pickId].filter((e) => !isPickStatusChangeError(e));
            }
        }

        return {
            ...state,
            pickErrors,
        };
    }),
    on(BetslipErrorsActions.removeOverallMaxWinPerUserExceededError, (state) => {
        const betslipErrors = state.betslipErrors.filter((e) => !isOverallMaxWinPerUserExceededError(e, OverallMaxWinPerUserHintType.Any));

        return {
            ...state,
            betslipErrors,
        };
    }),
    on(BetslipErrorsActions.cleanManualRemoveErrors, (state) => {
        const filterFunction = (error: BetslipError) => error.cleanStrategy !== CleanStrategy.Manually;

        return filterErrorState(
            state,
            (e) => filterFunction(e) || isOverallMaxWinPerUserExceededError(e, OverallMaxWinPerUserHintType.WithoutHint),
            filterFunction,
            filterFunction,
            filterFunction,
        );
    }),
    on(EditBetActions.init, BetslipPlaceBetActions.startedPlaceBet, () => {
        return defaultErrorState;
    }),
    on(BetslipErrorsActions.placeBetResponseErrors, (state, action) => {
        return {
            ...state,
            pickErrors: action.pickErrors,
            betslipErrors: action.betslipErrors,
            slipErrors: action.slipErrors,
        };
    }),
    on(BetslipErrorsActions.markAllNotifyUserErrorsAsSeen, (state, _) => {
        const mapFunction = <T extends BetslipError>(error: T): [T, boolean] => {
            if (isNotifyUserChangeError(error) && !error.userHasBeenNotified) {
                const copy = error.copy();

                if (isNotifyUserChangeError(copy)) {
                    copy.markAsSeen();
                }

                return [copy, true];
            }

            return [error, false];
        };

        const newState = modifyErrorState(state, mapFunction, mapFunction, mapFunction, mapFunction);

        if (!newState.isModified) {
            return state;
        }

        return {
            pickErrors: newState.pickErrors,
            betslipErrors: newState.betslipErrors,
            slipErrors: newState.slipErrors,
            betslipTypeErrors: newState.betslipTypeErrors,
        };
    }),
);
